import { useMemo } from "react";
import clsx from "clsx";
import { formatPrice } from "@/utils/utils";
import { formatDisplayDate, getOrderStatusLabel } from "@/helpers/helpers";
import { paymentMethodIdToNameMap, shippingMethodIdToNameMap } from "@/constants/mapping";
import CartItems from "@/components/features/cart/CartItems";
import { OrderStatus, type OrderDetails } from "@/types/order";

interface OrderDetailsProps {
  order: OrderDetails | null;
}

const OrderDetailsComponent = ({ order }: OrderDetailsProps) => {
  if (!order) return null;

  const { totals, billing_address, payment_method } = order;

  return (
    <div className="space-y-2">
      {/* 商品 */}
      <section className="brand-card brand-order-details-card space-y-0">
        <div className="brand-order-details-card-title">訂單商品</div>
        <CartItems items={order.items} showProductLink={true} />

        <div className="space-y-2 py-4">
          <div className="brand-text-sm flex items-center justify-between">
            <span>商品總金額</span>
            <span className="text-black">{formatPrice(totals?.total_items ?? 0)}</span>
          </div>
          <div className="brand-text-sm flex items-center justify-between">
            <span>運費</span>
            <span className="brand-text-sm text-black">
              {formatPrice(totals?.total_shipping ?? "")}
            </span>
          </div>
        </div>

        <hr className="-mx-6" />
        <div className="flex items-center justify-between pt-2">
          <span className="font-bold text-secondary">訂單付款金額</span>
          <span className="text-lg font-bold text-accent">
            {formatPrice(totals?.total_price ?? 0)}
          </span>
        </div>
      </section>

      {/* 訂單資訊 */}
      <section className="brand-card brand-order-details-card">
        <h2 className="brand-order-details-card-title">訂單資訊</h2>
        <div>
          <FieldLabel>訂單編號</FieldLabel>
          <FieldValue>{order.id}</FieldValue>
        </div>
        <div>
          <FieldLabel>訂單狀態</FieldLabel>
          <FieldValue>{getOrderStatusLabel(order.status)}</FieldValue>
        </div>
        <div>
          <FieldLabel>付款方式</FieldLabel>
          <FieldValue>{paymentMethodIdToNameMap[payment_method.method_id]}</FieldValue>
        </div>
        {order.status === OrderStatus.Refunded && (
          <>
            <div>
              <FieldLabel>已退款金額</FieldLabel>
              <FieldValue>
                {order?.refunds?.[0]?.total
                  ? formatPrice(Math.abs(Number(order.refunds[0].total)))
                  : "處理中，若有任何問題請聯繫店家"}
              </FieldValue>
            </div>
            <div>
              <FieldLabel>退款原因</FieldLabel>
              <FieldValue>{order?.refunds?.[0]?.reason ?? "未填寫"}</FieldValue>
            </div>
            {/* TODO 退款時間 */}
          </>
        )}

        <div>
          <FieldLabel>備貨日期</FieldLabel>
          <FieldValue>
            {order.date_preparing ? formatDisplayDate(order.date_preparing) : "尚未備貨"}
          </FieldValue>
        </div>

        <div>
          <FieldLabel>付款日期</FieldLabel>
          <FieldValue>
            {order.date_paid ? formatDisplayDate(order.date_paid) : "尚未付款"}
          </FieldValue>
        </div>

        <div>
          <FieldLabel>訂單成立日期</FieldLabel>
          <FieldValue>{formatDisplayDate(order.date_created)}</FieldValue>
        </div>
      </section>

      {/* Email */}
      <section className="brand-card brand-order-details-card">
        <h2 className="brand-order-details-card-title">訂購人聯絡資訊</h2>
        <div>
          <FieldLabel>電子信箱</FieldLabel>
          <FieldValue>{billing_address.email}</FieldValue>
        </div>
      </section>

      {/* Shipping */}
      <section className="brand-card brand-order-details-card">
        <h2 className="brand-order-details-card-title">寄送資訊</h2>
        <div>
          <FieldLabel>寄送方式</FieldLabel>
          <FieldValue>{shippingMethodIdToNameMap[order.shipping_methods[0].method_id]}</FieldValue>
        </div>
        <hr className="-mx-6" />
        <ShippingDetails order={order} />
      </section>

      {/* 備註 */}
      <section className="brand-card brand-order-details-card">
        <h2 className="brand-order-details-card-title">訂單備註</h2>
        <div>
          <FieldLabel>{order.customer_note}</FieldLabel>
        </div>
      </section>
    </div>
  );
};

export const FieldLabel = ({
  children,
  classes,
}: {
  children: React.ReactNode;
  classes?: string;
}) => <p className={clsx("brand-order-details-field-label", classes)}>{children}</p>;

export const FieldValue = ({
  children,
  classes,
}: {
  children: React.ReactNode;
  classes?: string;
}) => <p className={clsx("brand-order-details-field-value", classes)}>{children}</p>;

const ShippingDetails = ({ order }: { order: OrderDetails }) => {
  const { shipping_address, shipping_methods } = order;

  const isCVSShipping = useMemo(
    () => order?.shipping_methods[0].method_id.includes("Wooecpay_Logistic_CVS") ?? false,
    [order],
  );

  const cvsDetails = shipping_methods[0].cvs_details;

  if (isCVSShipping && cvsDetails) {
    /* CVS */
    return (
      <div className="space-y-1">
        <div className="flex">
          <FieldLabel>收件人：</FieldLabel>
          <FieldValue>{cvsDetails.cvs_receiver_name}</FieldValue>
        </div>

        <div className="flex">
          <FieldLabel>收件人手機：</FieldLabel>
          <FieldValue>{cvsDetails.cvs_receiver_phone}</FieldValue>
        </div>

        <div className="flex">
          <FieldLabel>超商名稱：</FieldLabel>
          <FieldValue>{cvsDetails.cvs_store_name}</FieldValue>
        </div>

        <div className="flex">
          <FieldLabel>超商編號：</FieldLabel>
          <FieldValue>{cvsDetails.cvs_store_id}</FieldValue>
        </div>

        <div className="flex">
          <FieldLabel>超商地址：</FieldLabel>
          <FieldValue>{cvsDetails.cvs_store_address}</FieldValue>
        </div>
      </div>
    );
  } else {
    /* 郵寄 */
    return (
      <div className="space-y-1">
        <div className="flex">
          <FieldLabel>收件人：</FieldLabel>
          <FieldValue>
            {shipping_address.last_name}
            {shipping_address.first_name}
          </FieldValue>
        </div>

        <div className="flex">
          <FieldLabel>收件人手機：</FieldLabel>
          <FieldValue>{shipping_address.phone}</FieldValue>
        </div>

        <div className="flex">
          <FieldLabel>地址：</FieldLabel>
          <FieldValue>
            {shipping_address.postcode} {shipping_address.state}
            {shipping_address.city}
            {shipping_address.address_1}
          </FieldValue>
        </div>

        <div className="flex">
          <FieldLabel>公司名稱：</FieldLabel>
          <FieldValue>{shipping_address.company ? shipping_address.company : "無"}</FieldValue>
        </div>
      </div>
    );
  }
};

export default OrderDetailsComponent;
